import {
    useState,
    useEffect,
    useCallback,
    useRef,
    useMemo,
    useImperativeHandle,
    forwardRef,
    type DragEvent,
} from "react";
import apiClient from "@/lib/api/apiClient";
import { env } from "@/config/env";
import { formatApiError, showError, showSuccess } from "@/lib/utils/notifications";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import {
    companyEditConfirmPresentation,
    EVENT_REASONS_MAX_LENGTH,
    LR_EVENT_API_KEYS,
    LR_ROW_ID_KEY,
    YARDI_EVENT_API_KEYS,
    YARDI_ROW_ID_KEY,
} from "@/features/company/constants";
import {
    isTruthyFlag,
    isYardiApiMaskedField,
    parseLrEventParameters,
    getDataFetchRangeByDaysError,
    newYardiRowId,
    withYardiRowId,
    stripYardiRowIds,
    newLrRowId,
    withLrRowId,
    stripLrRowIds,
    moveItemInArray,
    getDuplicateEventActionRowIndexes,
    hasDuplicateEventActionCombinations,
    normalizeConfigEventRowStrings,
    computeLrShortcodePopupPosition,
    lrShortcodeTriggerTestId,
    measureLrShortcodePopupHeight,
} from "./configTabHelpers";

import { Server } from "lucide-react";
import { SectionCard } from "@/components/ui/SectionCard";
import { ApiTokenEndpointSection } from "./companyConfigEmbed/ApiTokenEndpointSection";
import { LrEventConfigTable } from "./companyConfigEmbed/LrEventConfigTable";
import { LrShortcodePortal } from "./companyConfigEmbed/LrShortcodePortal";
import { YardiEventConfigTable } from "./companyConfigEmbed/YardiEventConfigTable";
import { YardiServerConfigSection } from "./companyConfigEmbed/YardiServerConfigSection";
import type {
    CompanyConfigEmbedHandle,
    CompanyConfigEmbedProps,
    LrEventConfigRow,
    LrEventTypeOption,
    LrShortcodeTooltipState,
    StaffAgentOption,
    YardiEventConfigRow,
    YardiEventTypeOption,
    YardiLrActionOption,
} from "../../types";

export const CompanyConfigEmbed = forwardRef<CompanyConfigEmbedHandle, CompanyConfigEmbedProps>(
    function CompanyConfigEmbed({ companyId, companyDetails }, ref) {
    const [isLoading, setIsLoading] = useState(false);
    const [staffList, setStaffList] = useState<StaffAgentOption[]>([]);
    const [apiToken, setApiToken] = useState("");

    /** Same as Angular `configFormUserName` / `configFormServerName` / `configFormDatabase` — shown as placeholder, not as input value when masked. */
    const [yardiSecretPlaceholders, setYardiSecretPlaceholders] = useState({
        user_name: "",
        password: "",
        server_name: "",
        database: "",
    });

    // Form State
    const [config, setConfig] = useState({
        user_name: "",
        password: "",
        server_name: "",
        database: "",
        client_url: "",
        environment: "",
        version_number: "",
        data_fetch_range_by_days: "",
        import_pmc_data: 0,
        lease_document_type: "",
        agent_id: "",
    });

    const [yardiEvents, setYardiEvents] = useState<YardiEventConfigRow[]>([]);
    const [lrEvents, setLrEvents] = useState<LrEventConfigRow[]>([]);

    /** Portal popup (table has overflow:hidden — fixed position like Angular absolute beside row). */
    const [lrShortcodeTooltip, setLrShortcodeTooltip] = useState<LrShortcodeTooltipState>(null);

    /** LR row: show Yardi "deny guest card" info panel (Angular `visibleLrReasonTooltips`). */
    const [visibleLrDenyGuestTooltips, setVisibleLrDenyGuestTooltips] = useState<boolean[]>([]);
    /** Yardi Event Config row: same helper when Take Action is Deny Guest Card (Angular `visibleYardiReasonTooltips`). */
    const [visibleYardiDenyGuestTooltips, setVisibleYardiDenyGuestTooltips] = useState<boolean[]>([]);
    /** LR comment touched/dirty — show required error like Angular reactive forms. */
    const [lrCommentInteracted, setLrCommentInteracted] = useState<boolean[]>([]);
    /** Yardi Event Config comment — same interaction model as LR. */
    const [yardiCommentInteracted, setYardiCommentInteracted] = useState<boolean[]>([]);
    /**
     * Set when Save is clicked — inline errors on Config tab (Angular `validateConfigForm` /
     * `validateConfigFormField` + marking row controls touched/dirty on submit).
     */
    const [lrSaveAttempted, setLrSaveAttempted] = useState(false);
    /** Angular: control.touched — true after user blurs the data_fetch_range_by_days input. */
    const [dataFetchRangeTouched, setDataFetchRangeTouched] = useState(false);
    /** Angular: appService.notify(error.error, 0) on generateToken failure → shown as ConfirmDialog */
    const [tokenErrorMessage, setTokenErrorMessage] = useState<string | null>(null);
    /** Popup when user tries to delete the only LR row. */
    const [showLrMinRowDialog, setShowLrMinRowDialog] = useState(false);
    /** Popup when user tries to delete the only Yardi row (same pattern as LR). */
    const [showYardiMinRowDialog, setShowYardiMinRowDialog] = useState(false);
    const yardiDragFromRef = useRef<number | null>(null);
    const [yardiDragOverIndex, setYardiDragOverIndex] = useState<number | null>(null);
    const [yardiDraggingIndex, setYardiDraggingIndex] = useState<number | null>(null);
    /** Angular `rowErrors` when `event.currentIndex === 0` on Yardi drop. */
    const [yardiRowReorderErrors, setYardiRowReorderErrors] = useState<Record<number, string>>({});

    const lrDragFromRef = useRef<number | null>(null);
    const [lrDragOverIndex, setLrDragOverIndex] = useState<number | null>(null);
    const [lrDraggingIndex, setLrDraggingIndex] = useState<number | null>(null);

    // Backend may expose camelCase or snake_case on company payload (and occasionally nest under `data`).
    const companyConfig =
        companyDetails?.companyConfig ??
        companyDetails?.company_config ??
        companyDetails?.data?.companyConfig ??
        companyDetails?.data?.company_config ??
        null;

    const attachmentTypes = companyConfig?.attachmentTypes ?? companyConfig?.attachment_types ?? [];
    const availableYardiEvents =
        (companyConfig?.yardiEventTypes ?? companyConfig?.yardi_event_types)?.filter((e: YardiEventTypeOption) =>
            isTruthyFlag(e.enabled),
        ) || [];
    const availableLrEvents = useMemo(
        () =>
            (companyConfig?.yardiLrEventTypes ?? companyConfig?.yardi_lr_event_types)?.filter((e: LrEventTypeOption) =>
                isTruthyFlag(e.enabled),
            ) || [],
        [companyConfig],
    );
    const availableYardiActions =
        (companyConfig?.yardiLrActions ?? companyConfig?.yardi_lr_actions)?.filter((a: YardiLrActionOption) =>
            isTruthyFlag(a.enabled_for_yardi_event_config),
        ) || [];
    const availableLrActions =
        (companyConfig?.yardiLrActions ?? companyConfig?.yardi_lr_actions)?.filter((a: YardiLrActionOption) =>
            isTruthyFlag(a.enabled_for_lr_event_config),
        ) || [];

    const yardiDuplicateRowIndexes = useMemo(
        () => getDuplicateEventActionRowIndexes(yardiEvents),
        [yardiEvents],
    );
    const lrDuplicateRowIndexes = useMemo(
        () => getDuplicateEventActionRowIndexes(lrEvents),
        [lrEvents],
    );

    /**
     * Public URL Yardi must call — same host as Angular `environment.endPoint` for local dev
     * (`https://api.react.libertyrent.com` in `environment.ts`) and the Vite proxy target from
     * `VITE_API_BASE_URL`. Prefer `VITE_YARDI_PUBLIC_API_URL` when a deploy needs a different public origin.
     */
    const yardiConfigDisplayUrl = useMemo(() => {
        const override = (import.meta.env.VITE_YARDI_PUBLIC_API_URL as string | undefined)?.trim();
        const apiFromEnv = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim();
        const base = String(override || apiFromEnv || env.apiBaseUrl).replace(/\/+$/, "");
        return `${base}/yardi_config/${companyId}`;
    }, [companyId]);

    useEffect(() => {
        if (companyConfig) {
            const cfg = companyConfig;
            const rawUser = cfg.user_name || "";
            const rawPass = cfg.password || "";
            const rawServer = cfg.server_name || "";
            const rawDb = cfg.database || "";
            setYardiSecretPlaceholders({
                user_name: rawUser,
                password: rawPass,
                server_name: rawServer,
                database: rawDb,
            });
            setConfig({
                user_name: isYardiApiMaskedField(rawUser) ? "" : rawUser,
                password: isYardiApiMaskedField(rawPass) ? "" : rawPass,
                server_name: isYardiApiMaskedField(rawServer) ? "" : rawServer,
                database: isYardiApiMaskedField(rawDb) ? "" : rawDb,
                client_url: cfg.client_url || "",
                environment: cfg.environment || "",
                version_number: cfg.version_number || "",
                data_fetch_range_by_days: String(cfg.data_fetch_range_by_days ?? ""),
                /** API may send 0/1 as string; normalize so Yes/No UI (select) reflects saved value. */
                import_pmc_data: Number(cfg.import_pmc_data) === 1 ? 1 : 0,
                lease_document_type: cfg.lease_document_type || "",
                agent_id: String(cfg.fk_agent_id ?? ""),
            });
            setApiToken(cfg.api_token || "");

            // Event Rows
            const yardiSelected = cfg.selected_yardi_event_types;
            if (yardiSelected && yardiSelected.length > 0) {
                setYardiEvents(
                    yardiSelected.map((r) => withYardiRowId({ ...r }) as YardiEventConfigRow),
                );
            } else {
                addYardiRow();
            }

            const lrSelected = cfg.selected_lr_event_types;
            if (lrSelected && lrSelected.length > 0) {
                setLrEvents(lrSelected.map((r) => withLrRowId({ ...r }) as LrEventConfigRow));
            } else {
                // Match Yardi block above: always have at least one configurable row so validation can run (Angular always has ≥1 row in practice when users configure LR events).
                setLrEvents([
                    {
                        event_type: "",
                        lr_action: "",
                        event_reasons: "",
                        [LR_ROW_ID_KEY]: newLrRowId(),
                    },
                ]);
            }
            setLrSaveAttempted(false);
            setDataFetchRangeTouched(false);
        } else if (companyDetails) {
            // Company loaded but nested config missing (or not yet merged into GET /company) — still show all sections with defaults.
            setYardiSecretPlaceholders({ user_name: "", password: "", server_name: "", database: "" });
            setConfig({
                user_name: "",
                password: "",
                server_name: "",
                database: "",
                client_url: "",
                environment: "",
                version_number: "",
                data_fetch_range_by_days: "",
                import_pmc_data: 0,
                lease_document_type: "",
                agent_id: "",
            });
            setApiToken("");
            setYardiEvents([
                {
                    event_type: "",
                    event_criteria: "",
                    lr_action: "sendInvite",
                    event_reasons: "",
                    [YARDI_ROW_ID_KEY]: newYardiRowId(),
                },
            ]);
            setLrEvents([
                {
                    event_type: "",
                    lr_action: "",
                    event_reasons: "",
                    [LR_ROW_ID_KEY]: newLrRowId(),
                },
            ]);
            setLrSaveAttempted(false);
            setDataFetchRangeTouched(false);
        }
        loadStaffs();
    }, [companyDetails]);

    useEffect(() => {
        setYardiCommentInteracted((prev) =>
            Array.from({ length: yardiEvents.length }, (_, i) => (i < prev.length ? prev[i] : false)),
        );
        setVisibleYardiDenyGuestTooltips((prev) =>
            Array.from({ length: yardiEvents.length }, (_, i) => (i < prev.length ? prev[i] : false)),
        );
    }, [yardiEvents.length]);

    useEffect(() => {
        setVisibleLrDenyGuestTooltips((prev) =>
            Array.from({ length: lrEvents.length }, (_, i) => (i < prev.length ? prev[i] : false)),
        );
        setLrCommentInteracted((prev) =>
            Array.from({ length: lrEvents.length }, (_, i) => (i < prev.length ? prev[i] : false)),
        );
    }, [lrEvents.length]);

    const loadStaffs = async () => {
        try {
            const response = await apiClient.get(`/api/property_staff`, {
                params: { company_id: companyId, roles: "company" }
            });
            setStaffList((response.data || []) as StaffAgentOption[]);
        } catch (error) {
            console.error("Error loading staffs:", error);
        }
    };

    const generateToken = async () => {
        setIsLoading(true);
        try {
            const response = await apiClient.get(`/api/yardi_api_token/${companyId}`);
            setApiToken(response.data.token);
        } catch (error: unknown) {
            setTokenErrorMessage(formatApiError(error));
        } finally {
            setIsLoading(false);
        }
    };

    const addYardiRow = () => {
        const isFirst = yardiEvents.length === 0;
        setYardiEvents([
            ...yardiEvents,
            {
                event_type: "",
                event_criteria: "",
                lr_action: isFirst ? "sendInvite" : "",
                event_reasons: "",
                [YARDI_ROW_ID_KEY]: newYardiRowId(),
            },
        ]);
    };

    const removeYardiRow = (index: number) => {
        if (yardiEvents.length <= 1) {
            setShowYardiMinRowDialog(true);
            return;
        }
        setYardiEvents((prev) => {
            const next = prev.filter((_, i) => i !== index);
            if (index === 0 && next[0]) {
                const r0 = { ...next[0], lr_action: "sendInvite", event_reasons: "" };
                return [r0, ...next.slice(1)];
            }
            return next;
        });
        setYardiCommentInteracted((prev) => prev.filter((_, i) => i !== index));
        setVisibleYardiDenyGuestTooltips((prev) => prev.filter((_, i) => i !== index));
    };

    const handleYardiRowDragEnd = useCallback(() => {
        yardiDragFromRef.current = null;
        setYardiDraggingIndex(null);
        setYardiDragOverIndex(null);
    }, []);

    const handleYardiRowDragStart = useCallback((e: DragEvent, idx: number) => {
        /** Angular CDK: every row has `cdkDrag`; only drops with `currentIndex === 0` are rejected. */
        yardiDragFromRef.current = idx;
        setYardiDraggingIndex(idx);
        e.dataTransfer.effectAllowed = "move";
        // Required for Firefox; some browsers ignore empty payloads.
        e.dataTransfer.setData("text/plain", `yardi-row:${idx}`);
    }, []);

    const handleYardiRowDrop = useCallback((e: DragEvent, dropIndex: number) => {
        e.preventDefault();
        const from = yardiDragFromRef.current;
        yardiDragFromRef.current = null;
        setYardiDraggingIndex(null);
        setYardiDragOverIndex(null);
        if (from == null) return;

        // Angular `drop()`: if (event.currentIndex === 0) { rowErrors[draggedIndex] = '...'; return; }
        if (dropIndex === 0) {
            setYardiRowReorderErrors((prev) => ({
                ...prev,
                [from]: "Reordering of first row is not applicable.",
            }));
            setTimeout(() => {
                setYardiRowReorderErrors((prev) => {
                    const next = { ...prev };
                    delete next[from];
                    return next;
                });
            }, 2000);
            return;
        }
        if (from === dropIndex) return;

        setYardiEvents((prevEv) => {
            const nextEv = moveItemInArray(prevEv, from, dropIndex);
            setYardiCommentInteracted((prevInt) => {
                const padded = Array.from({ length: prevEv.length }, (_, i) => prevInt[i] ?? false);
                return moveItemInArray(padded, from, dropIndex);
            });
            setVisibleYardiDenyGuestTooltips((prevVis) => {
                const padded = Array.from({ length: prevEv.length }, (_, i) => prevVis[i] ?? false);
                return moveItemInArray(padded, from, dropIndex);
            });
            return nextEv;
        });
    }, []);

    const handleLrRowDragEnd = useCallback(() => {
        lrDragFromRef.current = null;
        setLrDraggingIndex(null);
        setLrDragOverIndex(null);
    }, []);

    const handleLrRowDragStart = useCallback((e: DragEvent, idx: number) => {
        lrDragFromRef.current = idx;
        setLrDraggingIndex(idx);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", `lr-row:${idx}`);
    }, []);

    const handleLrRowDrop = useCallback((e: DragEvent, dropIndex: number) => {
        e.preventDefault();
        const from = lrDragFromRef.current;
        lrDragFromRef.current = null;
        setLrDraggingIndex(null);
        setLrDragOverIndex(null);
        if (from == null) return;
        if (from === dropIndex) return;

        setLrEvents((prevEv) => {
            const nextEv = moveItemInArray(prevEv, from, dropIndex);
            setLrCommentInteracted((prevInt) => {
                const padded = Array.from({ length: prevEv.length }, (_, i) => prevInt[i] ?? false);
                return moveItemInArray(padded, from, dropIndex);
            });
            setVisibleLrDenyGuestTooltips((prev) => {
                const padded = Array.from({ length: prevEv.length }, (_, i) => prev[i] ?? false);
                return moveItemInArray(padded, from, dropIndex);
            });
            return nextEv;
        });
    }, []);

    const addLrRow = () => {
        setLrEvents([
            ...lrEvents,
            {
                event_type: "",
                lr_action: "",
                event_reasons: "",
                [LR_ROW_ID_KEY]: newLrRowId(),
            },
        ]);
    };

    const removeLrRow = (index: number) => {
        if (lrEvents.length <= 1) {
            setShowLrMinRowDialog(true);
            return;
        }
        setLrEvents(lrEvents.filter((_, i) => i !== index));
        setLrShortcodeTooltip(null);
        setVisibleLrDenyGuestTooltips((prev) => prev.filter((_, i) => i !== index));
        setLrCommentInteracted((prev) => prev.filter((_, i) => i !== index));
    };

    const closeLrDenyGuestTooltip = (index: number) => {
        setVisibleLrDenyGuestTooltips((prev) => {
            const next = [...prev];
            if (index < next.length) next[index] = false;
            return next;
        });
    };

    const closeYardiDenyGuestTooltip = (index: number) => {
        setVisibleYardiDenyGuestTooltips((prev) => {
            const next = [...prev];
            if (index < next.length) next[index] = false;
            return next;
        });
    };

    const getActionCommentRequired = (actionType: string | undefined, pool: YardiLrActionOption[]) => {
        const action = pool.find((a) => a.action_type === actionType);
        return isTruthyFlag(action?.is_comment_required);
    };

    const getEventCriteriaRequired = (eventType: string | undefined) => {
        const event = availableYardiEvents.find((e) => e.value === eventType);
        return isTruthyFlag(event?.is_event_criteria_required);
    };

    /** LR Event Config: at least one row, required fields per row — mirrors Angular `submitCompanyConfig` + row validators. */
    const validateLrEventsForSave = (
        rows: LrEventConfigRow[],
        lrActionsPool: YardiLrActionOption[],
    ): string | null => {
        if (rows.length < 1) {
            return "At least one LR event row is required.";
        }
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (!String(r.event_type ?? "").trim()) {
                return "Event is required.";
            }
            if (!String(r.lr_action ?? "").trim()) {
                return "Action is required.";
            }
            if (
                getActionCommentRequired(r.lr_action, lrActionsPool) &&
                !String(r.event_reasons ?? "").trim()
            ) {
                return "Action comment is required.";
            }
            if (String(r.event_reasons ?? "").length > EVENT_REASONS_MAX_LENGTH) {
                return "Action comment must not exceed 240 characters.";
            }
        }
        if (hasDuplicateEventActionCombinations(rows)) {
            return "Event/Action combination is duplicate.";
        }
        return null;
    };

    /** Yardi Event Config row validation — mirrors Angular `submitCompanyConfig` + `Validators.maxLength(240)` on comments. */
    const validateYardiEventsForSave = (
        rows: YardiEventConfigRow[],
        yardiActionsPool: YardiLrActionOption[],
    ): string | null => {
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (!String(r.event_type ?? "").trim()) {
                return "Event is required.";
            }
            if (!String(r.lr_action ?? "").trim()) {
                return "Action is required.";
            }
            if (
                getEventCriteriaRequired(r.event_type) &&
                !String(r.event_criteria ?? "").trim()
            ) {
                return "Event criteria is required.";
            }
            if (
                getActionCommentRequired(r.lr_action, yardiActionsPool) &&
                !String(r.event_reasons ?? "").trim()
            ) {
                return "Action comment is required.";
            }
            if (String(r.event_reasons ?? "").length > EVENT_REASONS_MAX_LENGTH) {
                return "Action comment must not exceed 240 characters.";
            }
        }
        if (hasDuplicateEventActionCombinations(rows)) {
            return "Event/Action combination is duplicate.";
        }
        return null;
    };

    const performCompanyConfigSave = async () => {
        try {
            /** Never POST mask-shaped secrets (parity with Angular empty controls when unchanged). */
            const sanitizeYardiSecretInput = (v: string) => (isYardiApiMaskedField(v) ? "" : v);
            const payload = {
                ...config,
                user_name: sanitizeYardiSecretInput(config.user_name),
                password: sanitizeYardiSecretInput(config.password),
                server_name: sanitizeYardiSecretInput(config.server_name),
                database: sanitizeYardiSecretInput(config.database),
                /** Edited on Preferences tab; pass through so Yardi config save does not clear it. */
                itrratio: companyDetails?.itrratio ?? "",
                selected_yardi_event_types: stripYardiRowIds(yardiEvents).map((r) =>
                    normalizeConfigEventRowStrings(r as Record<string, unknown>, YARDI_EVENT_API_KEYS),
                ),
                selected_lr_event_types: stripLrRowIds(lrEvents).map((r) =>
                    normalizeConfigEventRowStrings(r as Record<string, unknown>, LR_EVENT_API_KEYS),
                ),
            };
            await apiClient.post(`/api/company/config/${companyId}`, payload);
            showSuccess("Company Config Edited Successfully!");
            setLrSaveAttempted(false);
        } catch (error: unknown) {
            showError(formatApiError(error));
            throw error;
        }
    };

    /**
     * Shared validation for Config (Angular `submitCompanyConfig` checks). Returns true if the form can be saved.
     */
    const validateConfigTabForSubmit = (): boolean => {
        setLrSaveAttempted(true);
        if (!staffList || staffList.length === 0) {
            return false;
        }
        if (getDataFetchRangeByDaysError(config.data_fetch_range_by_days) !== null) {
            return false;
        }
        if (!String(config.agent_id ?? "").trim()) {
            return false;
        }
        const yardiErr = validateYardiEventsForSave(yardiEvents, availableYardiActions);
        if (yardiErr !== null) {
            setYardiCommentInteracted(() => yardiEvents.map(() => true));
            return false;
        }
        const lrErr = validateLrEventsForSave(lrEvents, availableLrActions);
        if (lrErr !== null) {
            setLrCommentInteracted(() => lrEvents.map(() => true));
            return false;
        }
        return true;
    };

    useImperativeHandle(ref, () => ({
        validateConfig: () => validateConfigTabForSubmit(),
        saveConfigWithoutValidation: () => performCompanyConfigSave(),
    }));

    const handleLrShortcodeInfoClick = useCallback(
        (idx: number, e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();
            e.stopPropagation();
            // Read geometry synchronously — React may clear synthetic event before async state runs.
            const r = e.currentTarget.getBoundingClientRect();
            setLrShortcodeTooltip((prev: LrShortcodeTooltipState) => {
                if (prev?.idx === idx) return null;
                const eventType = lrEvents[idx]?.event_type;
                let codes: Array<{ label: string; code: string }> | null = null;
                if (eventType) {
                    const selected = availableLrEvents.find(
                        (ev) => String(ev.event_type) === String(eventType),
                    );
                    if (selected) codes = parseLrEventParameters(selected.parameters);
                }
                const { top, left, placement } = computeLrShortcodePopupPosition(r);
                return { idx, top, left, placement, codes };
            });
        },
        [lrEvents, availableLrEvents],
    );

    useEffect(() => {
        if (!lrShortcodeTooltip) return;
        let removeListener: (() => void) | undefined;
        const tid = window.setTimeout(() => {
            const onDown = (ev: MouseEvent) => {
                const el = ev.target as HTMLElement;
                if (el.closest("[data-lr-shortcode-popup]")) return;
                if (el.closest("[data-lr-shortcode-trigger]")) return;
                setLrShortcodeTooltip(null);
            };
            document.addEventListener("mousedown", onDown);
            removeListener = () => document.removeEventListener("mousedown", onDown);
        }, 0);
        return () => {
            window.clearTimeout(tid);
            removeListener?.();
        };
    }, [lrShortcodeTooltip]);

    useEffect(() => {
        if (!lrShortcodeTooltip) return;
        const onKey = (ev: KeyboardEvent) => {
            if (ev.key === "Escape") setLrShortcodeTooltip(null);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [lrShortcodeTooltip]);

    /** Keep the fixed portal anchored to the info icon while the page (or nested overflow) scrolls. */
    useEffect(() => {
        if (!lrShortcodeTooltip) return;
        const rowIdx = lrShortcodeTooltip.idx;
        const syncPosition = () => {
            const trigger = document.querySelector<HTMLElement>(
                `[data-testid="${lrShortcodeTriggerTestId(rowIdx)}"]`,
            );
            if (!trigger) {
                setLrShortcodeTooltip(null);
                return;
            }
            const next = computeLrShortcodePopupPosition(
                trigger.getBoundingClientRect(),
                window,
                measureLrShortcodePopupHeight(),
            );
            setLrShortcodeTooltip((prev) => {
                if (!prev || prev.idx !== rowIdx) return prev;
                if (
                    prev.top === next.top &&
                    prev.left === next.left &&
                    prev.placement === next.placement
                ) {
                    return prev;
                }
                return { ...prev, ...next };
            });
        };
        window.addEventListener("scroll", syncPosition, true);
        window.addEventListener("resize", syncPosition);
        return () => {
            window.removeEventListener("scroll", syncPosition, true);
            window.removeEventListener("resize", syncPosition);
        };
    }, [lrShortcodeTooltip?.idx]);

    const handleLrShortcodePositionChange = useCallback(
        (pos: { top: number; left: number; placement: "right" | "left" }) => {
            setLrShortcodeTooltip((prev) => {
                if (!prev) return prev;
                if (
                    prev.top === pos.top &&
                    prev.left === pos.left &&
                    prev.placement === pos.placement
                ) {
                    return prev;
                }
                return { ...prev, ...pos };
            });
        },
        [],
    );

    const dataFetchRangeFieldError = getDataFetchRangeByDaysError(config.data_fetch_range_by_days);

    return (
        <div data-testid="company-config-embed-root" className="flex flex-col gap-4">
            <SectionCard
                icon={Server}
                title="Yardi Config"
                testId="company-config-yardi-config-section"
            >
                <YardiServerConfigSection
                    config={config}
                    setConfig={setConfig}
                    yardiSecretPlaceholders={yardiSecretPlaceholders}
                    attachmentTypes={attachmentTypes}
                    staffList={staffList}
                    lrSaveAttempted={lrSaveAttempted}
                    dataFetchRangeFieldError={dataFetchRangeFieldError}
                    dataFetchRangeTouched={dataFetchRangeTouched}
                    onDataFetchRangeBlur={() => setDataFetchRangeTouched(true)}
                />
                <ApiTokenEndpointSection
                    yardiConfigDisplayUrl={yardiConfigDisplayUrl}
                    apiToken={apiToken}
                    generateToken={generateToken}
                    isLoading={isLoading}
                />
                <YardiEventConfigTable
                    addYardiRow={addYardiRow}
                    yardiEvents={yardiEvents}
                    setYardiEvents={setYardiEvents}
                    availableYardiEvents={availableYardiEvents}
                    availableYardiActions={availableYardiActions}
                    yardiDuplicateRowIndexes={yardiDuplicateRowIndexes}
                    lrSaveAttempted={lrSaveAttempted}
                    yardiRowReorderErrors={yardiRowReorderErrors}
                    setYardiDragOverIndex={setYardiDragOverIndex}
                    yardiDraggingIndex={yardiDraggingIndex}
                    yardiDragOverIndex={yardiDragOverIndex}
                    handleYardiRowDrop={handleYardiRowDrop}
                    handleYardiRowDragStart={handleYardiRowDragStart}
                    handleYardiRowDragEnd={handleYardiRowDragEnd}
                    getEventCriteriaRequired={getEventCriteriaRequired}
                    getActionCommentRequired={getActionCommentRequired}
                    yardiCommentInteracted={yardiCommentInteracted}
                    setYardiCommentInteracted={setYardiCommentInteracted}
                    visibleYardiDenyGuestTooltips={visibleYardiDenyGuestTooltips}
                    setVisibleYardiDenyGuestTooltips={setVisibleYardiDenyGuestTooltips}
                    closeYardiDenyGuestTooltip={closeYardiDenyGuestTooltip}
                    removeYardiRow={removeYardiRow}
                />
                <LrEventConfigTable
                    addLrRow={addLrRow}
                    lrEvents={lrEvents}
                    setLrEvents={setLrEvents}
                    availableLrEvents={availableLrEvents}
                    availableLrActions={availableLrActions}
                    lrDuplicateRowIndexes={lrDuplicateRowIndexes}
                    lrSaveAttempted={lrSaveAttempted}
                    setLrDragOverIndex={setLrDragOverIndex}
                    lrDraggingIndex={lrDraggingIndex}
                    lrDragOverIndex={lrDragOverIndex}
                    handleLrRowDrop={handleLrRowDrop}
                    handleLrRowDragStart={handleLrRowDragStart}
                    handleLrRowDragEnd={handleLrRowDragEnd}
                    getActionCommentRequired={getActionCommentRequired}
                    lrCommentInteracted={lrCommentInteracted}
                    setLrCommentInteracted={setLrCommentInteracted}
                    setLrShortcodeTooltip={setLrShortcodeTooltip}
                    setVisibleLrDenyGuestTooltips={setVisibleLrDenyGuestTooltips}
                    visibleLrDenyGuestTooltips={visibleLrDenyGuestTooltips}
                    handleLrShortcodeInfoClick={handleLrShortcodeInfoClick}
                    closeLrDenyGuestTooltip={closeLrDenyGuestTooltip}
                    removeLrRow={removeLrRow}
                />
            </SectionCard>
            <LrShortcodePortal
                tooltip={lrShortcodeTooltip}
                onClose={() => setLrShortcodeTooltip(null)}
                onPositionChange={handleLrShortcodePositionChange}
            />
            <ConfirmDialog
                isOpen={showLrMinRowDialog}
                title="Validation"
                message="At least one LR event row is required."
                {...companyEditConfirmPresentation}
                primaryLabel="OK"
                onPrimary={() => setShowLrMinRowDialog(false)}
                secondaryLabel="Cancel"
                onSecondary={() => setShowLrMinRowDialog(false)}
                overlayDataTestId="company-config-embed-lr-min-row-confirm-overlay"
                primaryButtonDataTestId="company-config-embed-lr-min-row-confirm-ok-button"
                secondaryButtonDataTestId="company-config-embed-lr-min-row-confirm-cancel-button"
                closeButtonDataTestId="company-config-embed-lr-min-row-confirm-close-button"
            />
            <ConfirmDialog
                isOpen={showYardiMinRowDialog}
                title="Validation"
                message="At least one Yardi Event row is required."
                {...companyEditConfirmPresentation}
                primaryLabel="OK"
                onPrimary={() => setShowYardiMinRowDialog(false)}
                secondaryLabel="Cancel"
                onSecondary={() => setShowYardiMinRowDialog(false)}
                overlayDataTestId="company-config-embed-yardi-min-row-confirm-overlay"
                primaryButtonDataTestId="company-config-embed-yardi-min-row-confirm-ok-button"
                secondaryButtonDataTestId="company-config-embed-yardi-min-row-confirm-cancel-button"
                closeButtonDataTestId="company-config-embed-yardi-min-row-confirm-close-button"
            />
            {/* Angular: appService.notify(error.error, 0) on generateToken failure */}
            <ConfirmDialog
                isOpen={!!tokenErrorMessage}
                title="Validation Message"
                message=""
                htmlMessage={tokenErrorMessage ?? ""}
                {...companyEditConfirmPresentation}
                primaryLabel="OK"
                onPrimary={() => setTokenErrorMessage(null)}
                secondaryLabel="Cancel"
                onSecondary={() => setTokenErrorMessage(null)}
                overlayDataTestId="company-config-embed-token-error-confirm-overlay"
                primaryButtonDataTestId="company-config-embed-token-error-confirm-ok-button"
                secondaryButtonDataTestId="company-config-embed-token-error-confirm-cancel-button"
                closeButtonDataTestId="company-config-embed-token-error-confirm-close-button"
            />
        </div>
    );
});
